import React, { useState, useEffect } from 'react';
import { useTranslation } from '../utils/LanguageContext';
import Section from '../components/about/Section';
import ProcessList from '../components/about/ProcessList';
import Requirements from '../components/about/Requirements';
import Literature from '../components/about/Literature';
import Tools from '../components/about/Tools';
import Contact from '../components/about/Contact';

const AboutPage = () => {
  const { t, language } = useTranslation();
  const [aboutData, setAboutData] = useState(null);
  const [organizationsData, setOrganizationsData] = useState(null);
  const [peopleData, setPeopleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const fetchWithFallback = async (endpoint) => {
          try {
            const response = await fetch(`/data_copy/type=data_course_master/language=${language}/${endpoint}__${language}.json`, {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              }
            });
            
            if (!response.ok && language !== 'en') {
              const enResponse = await fetch(`/data_copy/type=data_course_master/language=${language}/${endpoint}__en.json`);
              if (!enResponse.ok) throw new Error(t('error.loadingFailed'));
              return await enResponse.json();
            }
            
            return await response.json();
          } catch (error) {
            console.error(`Error fetching ${endpoint}:`, error);
            throw new Error(t('error.loadingFailed'));
          }
        };

        const [about, organizations, people] = await Promise.all([
          fetchWithFallback('course_about'),
          fetchWithFallback('course_organizations'),
          fetchWithFallback('course_people')
        ]);

        setAboutData(about);
        setOrganizationsData(organizations);
        setPeopleData(people);

      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [language, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-8"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
          <div className="text-center mt-4 text-gray-600">
            {t('loading')}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 p-4 rounded-md">
            <h3 className="text-sm font-medium text-red-800">{t('error.loadingFailed')}</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!aboutData || !organizationsData || !peopleData) return null;

  // Find relevant sections by name_normed
  const courseIntro = aboutData.find(item => item.name_normed === 'digital_horizon_phenomenology_of_modern_technologies');
  const setupProcess = aboutData.find(item => item.name_normed === 'course_setup_process');
  const deliveryMethods = aboutData.find(item => item.name_normed === 'course_delivery_methods');
  const requirements = aboutData.find(item => item.name_normed === 'entry_requirements');
  const companySupport = aboutData.find(item => item.name_normed === 'company_support');
  const literature = aboutData.find(item => item.name_normed === 'core_literature_rendered');

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <Section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {courseIntro?.name}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {courseIntro?.description}
            </p>
          </Section>

          {setupProcess && (
            <Section title={setupProcess.name}>
              <ProcessList items={setupProcess.values || []} />
            </Section>
          )}

          {deliveryMethods && (
            <Section title={deliveryMethods.name}>
              <ProcessList items={deliveryMethods.values || []} />
            </Section>
          )}

          {requirements && (
            <Section title={requirements.name}>
              <Requirements items={requirements.values || []} />
            </Section>
          )}

          {companySupport && (
            <Section title={companySupport.name}>
              <div className="space-y-6">
                <p className="text-gray-600">{companySupport.description}</p>
                <Tools items={companySupport.values || []} />
              </div>
            </Section>
          )}

          {literature && (
            <Section title={literature.name}>
              <Literature items={literature.values || []} />
            </Section>
          )}

          <Section title={t('aboutPage.courseProvider')}>
            <Contact data={organizationsData} people={peopleData} />
          </Section>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;